import React from 'react';
import { Plus, Trash2, Image } from 'lucide-react';
import ColorEditor from '../ColorEditor';
import { resolveImageUrl } from '../../utils/imageUtils';

const HeaderEditor = ({ content, onContentChange, onMultipleContentChanges, onArrayFieldChange, onAddArrayItem, onRemoveArrayItem, uploading, onImageUpload, onOpenImagePicker, site, onCreateCustomTheme }) => {
  return (
    <div className="space-y-4">
      {/* Logo */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Logo/Brand Name
        </label>
        
        {/* Logo Type Toggle - Always visible */}
        <div className="flex items-center space-x-4 mb-3 p-2 bg-gray-50 rounded-lg border">
          <span className="text-xs font-medium text-gray-600">Type:</span>
          <label className="flex items-center">
            <input
              type="radio"
              name="logoType"
              value="text"
              checked={content.logoType !== 'image'}
              onChange={() => {
                // Batch all changes in one call to prevent race conditions
                onMultipleContentChanges({
                  logoType: 'text',
                  logoImage: '',
                  logoImageUrl: '',
                  logoImageCredit: ''
                });
              }}
              className="mr-2"
            />
            <span className="text-sm">Text Logo</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="logoType"
              value="image"
              checked={content.logoType === 'image'}
              onChange={(e) => {
                onContentChange('logoType', 'image');
              }}
              className="mr-2"
            />
            <span className="text-sm">Image Logo</span>
          </label>
        </div>

        {/* Logo Height */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Logo Height
          </label>
          <select
            value={content.logoHeight || 'medium'}
            onChange={(e) => onContentChange('logoHeight', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
            <option value="xl">Extra Large</option>
          </select>
        </div>

        {/* Text Logo Input */}
        {content.logoType !== 'image' && (
          <input
            type="text"
            value={content.companyName || content.businessName || ''}
            onChange={(e) => {
              onContentChange('companyName', e.target.value);
              onContentChange('businessName', e.target.value);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Your Business Name"
          />
        )}

        {/* Image Logo Upload */}
        {content.logoType === 'image' && content.logoImage && (
          <div className="space-y-3">
            {/* Logo Preview */}
            <div className="p-2 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center space-x-2">
                <div 
                  className="h-15 w-16 bg-white rounded-lg border shadow-sm flex-shrink-0 cursor-pointer hover:shadow-md transition-shadow relative group"
                  onClick={() => document.getElementById('logoReplaceInput').click()}
                  title="Click to upload new logo"
                >
                  <img 
                    src={resolveImageUrl(content.logoImageUrl || content.logoImage)} 
                    alt="Logo preview" 
                    className="h-full w-full object-contain rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all flex items-center justify-center">
                    <Image className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => onOpenImagePicker && onOpenImagePicker('logoImage')}
                    disabled={uploading}
                    className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition disabled:opacity-50 flex items-center space-x-1"
                  >
                    <Image className="w-3 h-3" />
                    <span>Unsplash</span>
                  </button>
                </div>
                <button
                  onClick={() => {
                    onContentChange('logoImage', '');
                    onContentChange('logoImageUrl', '');
                    onContentChange('logoImageCredit', '');
                  }}
                  className="text-red-500 hover:text-red-700 transition p-1 rounded hover:bg-red-50"
                  title="Remove logo"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Hidden file input for replacing logo */}
            <input
              id="logoReplaceInput"
              type="file"
              accept="image/*"
              onChange={(e) => onImageUpload && onImageUpload(e.target.files[0], 'logoImage')}
              className="hidden"
              disabled={uploading}
            />

            {/* Logo Image Settings */}
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Logo Height
                </label>
                <select
                  value={content.logoHeight || 'medium'}
                  onChange={(e) => onContentChange('logoHeight', e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                >
                  <option value="small">Small (32px)</option>
                  <option value="medium">Medium (40px)</option>
                  <option value="large">Large (48px)</option>
                  <option value="xl">Extra Large (56px)</option>
                </select>
              </div>
            </div>
            
            {/* Attribution for Unsplash images */}
            {content.logoImageCredit && (
              <div className="text-xs text-gray-500">
                Photo by{' '}
                <a 
                  href={content.logoImageCredit.photographerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-500"
                >
                  {content.logoImageCredit.photographer}
                </a>
                {' '}on{' '}
                <a 
                  href="https://unsplash.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-500"
                >
                  Unsplash
                </a>
              </div>
            )}
          </div>
        )}

        {/* Upload/Image Picker (show when image mode selected but no image uploaded) */}
        {content.logoType === 'image' && !content.logoImage && (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
            <input
              id="logoImageInput"
              type="file"
              accept="image/*"
              onChange={(e) => onImageUpload && onImageUpload(e.target.files[0], 'logoImage')}
              className="hidden"
              disabled={uploading}
            />
            <div className="space-y-3">
              <Image className="mx-auto h-8 w-8 text-gray-400" />
              <div className="flex space-x-2 justify-center">
                <button
                  type="button"
                  onClick={() => document.getElementById('logoImageInput').click()}
                  disabled={uploading}
                  className="px-3 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition min-w-0 flex flex-col items-center justify-center"
                >
                  <Image className="w-3 h-3 mb-1" />
                  <span>{uploading ? 'Uploading...' : 'Upload'}</span>
                </button>
                <button
                  onClick={() => onOpenImagePicker && onOpenImagePicker('logoImage')}
                  className="px-3 py-2 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition min-w-0 flex flex-col items-center justify-center"
                  disabled={uploading}
                >
                  <Image className="w-3 h-3 mb-1" />
                  <span>Unsplash</span>
                </button>
              </div>
              <p className="text-xs text-gray-500">Upload your logo or choose from stock photos</p>
            </div>
          </div>
        )}

        {/* Fallback Text for Image Logo */}
        {content.logoType === 'image' && content.logoImage && (
          <div className="mt-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Fallback Text (for accessibility)
            </label>
            <input
              type="text"
              value={content.companyName || content.businessName || ''}
              onChange={(e) => {
                onContentChange('companyName', e.target.value);
                onContentChange('businessName', e.target.value);
              }}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              placeholder="Your Business Name"
            />
          </div>
        )}

        {/* Logo Position Control (always visible) */}
        <div className="mt-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Logo Position
          </label>
          <select
            value={content.logoPosition || 'left'}
            onChange={(e) => onContentChange('logoPosition', e.target.value)}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
          >
            <option value="left">Left (with text)</option>
            <option value="center">Center (logo only)</option>
          </select>
        </div>
      </div>

      {/* Navigation */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Navigation Links
        </label>
        <div className="space-y-2">
          {(content.navigation || ['Home', 'About', 'Services', 'Contact']).map((item, index) => (
            <div key={index} className="flex items-center space-x-2">
              <div className="flex-1 space-y-1 p-2 border border-gray-200 rounded">
                <input
                  type="text"
                  value={typeof item === 'string' ? item : (item.text || item.label || '')}
                  onChange={(e) => {
                    const newValue = typeof item === 'string' 
                      ? e.target.value 
                      : { ...item, text: e.target.value, label: e.target.value };
                    onArrayFieldChange('navigation', index, newValue);
                  }}
                  className="w-full bg-transparent border-none outline-none text-xs font-medium"
                  placeholder="Link text"
                />
                <input
                  type="text"
                  value={typeof item === 'string' ? `#${item.toLowerCase()}` : (item.url || item.link || `#${(item.text || item.label || '').toLowerCase()}`)}
                  onChange={(e) => {
                    const newValue = typeof item === 'string'
                      ? { text: item, url: e.target.value, label: item, link: e.target.value }
                      : { ...item, url: e.target.value, link: e.target.value };
                    onArrayFieldChange('navigation', index, newValue);
                  }}
                  className="w-full bg-transparent border-none outline-none text-xs text-gray-600"
                  placeholder="#section or https://..."
                />
              </div>
              <button
                onClick={() => onRemoveArrayItem('navigation', index)}
                className="text-gray-400 hover:text-red-500 flex-shrink-0"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
          <button
            onClick={() => onAddArrayItem('navigation', { text: 'New Link', url: '#new', label: 'New Link', link: '#new' })}
            className="flex items-center text-xs text-purple-600 hover:text-purple-700"
            disabled={(content?.navigation?.length || 4) >= 6}
          >
            <Plus className="w-3 h-3 mr-1" />
            Add link
          </button>
        </div>
      </div>

      {/* Button Settings */}
      <div className="space-y-2">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-medium text-gray-900">Header Button</label>
          <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
            <input 
              id="headerButtonToggle"
              type="checkbox" 
              checked={content.showButton !== false}
              onChange={(e) => onContentChange('showButton', e.target.checked)}
              className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
            />
            <label htmlFor="headerButtonToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
          </div>
        </div>
        {content.showButton !== false && (
          <>
            <input
              type="text"
              value={content.buttonText || content.primaryCTA?.text || ''}
              onChange={(e) => {
                onContentChange('buttonText', e.target.value);
                onContentChange('primaryCTA', { 
                  ...(content.primaryCTA || {}), 
                  text: e.target.value 
                });
              }}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              placeholder="Contact Us"
            />
            <input
              type="text"
              value={content.buttonUrl || content.primaryCTA?.link || ''}
              onChange={(e) => {
                onContentChange('buttonUrl', e.target.value);
                onContentChange('primaryCTA', { 
                  ...(content.primaryCTA || {}), 
                  link: e.target.value 
                });
              }}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              placeholder="#contact or https://..."
            />
          </>
        )}
      </div>

      {/* Header Style Options */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Header Style
        </label>
        <select
          value={content.style || 'clean'}
          onChange={(e) => onContentChange('style', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
        >
          <option value="clean">Clean</option>
          <option value="minimal">Minimal</option>
          <option value="bold">Bold</option>
          <option value="elegant">Elegant</option>
        </select>
      </div>

      {/* Header Layout */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Layout
        </label>
        <select
          value={content.layout || 'horizontal'}
          onChange={(e) => onContentChange('layout', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
        >
          <option value="horizontal">Horizontal</option>
          <option value="centered">Centered</option>
          <option value="stacked">Stacked</option>
        </select>
      </div>

      {/* Toggle Options */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-gray-900">Social Links</label>
          <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
            <input 
              id="socialLinksToggle"
              type="checkbox" 
              checked={content.showSocial !== false}
              onChange={(e) => onContentChange('showSocial', e.target.checked)}
              className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
            />
            <label htmlFor="socialLinksToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-gray-900">Fixed Header</label>
          <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
            <input 
              id="fixedHeaderToggle"
              type="checkbox" 
              checked={content.fixedHeader !== false}
              onChange={(e) => onContentChange('fixedHeader', e.target.checked)}
              className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
            />
            <label htmlFor="fixedHeaderToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-gray-900">Inverted Colors</label>
          <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
            <input 
              id="invertedColorsToggle"
              type="checkbox" 
              checked={content.invertedColors || false}
              onChange={(e) => onContentChange('invertedColors', e.target.checked)}
              className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
            />
            <label htmlFor="invertedColorsToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
          </div>
        </div>
      </div>

      {/* Color Theme - Always at bottom */}
      <ColorEditor
        value={content.colorScheme || 'default'}
        onChange={(value) => onContentChange('colorScheme', value)}
        label="Section Color Theme"
        showReset={true}
        resetValue="default"
        siteTheme={site?.design?.colorTheme ? {
          name: site.design.colorThemeName || 'Site Theme',
          colors: site.design.colorTheme
        } : null}
        customThemes={site?.design?.customThemes || []}
        onCreateCustomTheme={onCreateCustomTheme}
      />
    </div>
  );
};

export default HeaderEditor;